import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Colours from '@shared/ui/atoms/Colours';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { failureNotification } from "@shared/store/notification.duck";
import { FloorHeatMap, getFloorHeatMap } from '@features/reporting/services/OfficeHeatMapService';
import { HeatMapFloorPlan } from "./HeatMapFloorPlan";
import { DeskHeatTiles } from "./DeskHeatTiles";
import { HeatMapLegend } from "./HeatMapLegend";
import { areaRatio, deskRatio, roomRatio } from "./floorHeat";

interface Props {
  floorId: number;
  fromKey: string;
  toKey: string;
}

export function FloorHeatMapSection({floorId, fromKey, toKey}: Props) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [floor, setFloor] = useState<FloorHeatMap | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFloor(undefined);
    getFloorHeatMap(floorId, moment(fromKey), moment(toKey))
      .then(data => { if (!cancelled) setFloor(data); })
      .catch(() => { if (!cancelled) dispatch(failureNotification(t('reports-page.office-usage.heat-map.load-failed'))); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [floorId, fromKey, toKey, dispatch, t]);

  // Over capacity and no capacity only get a legend entry when something on the floor is one
  const ratios = useMemo(() => floor ? [
    ...floor.desks.map(desk => deskRatio(desk, floor.daysInRange)),
    ...floor.areas.map(area => areaRatio(area, floor.daysInRange)),
    ...floor.meetingRooms.map(roomRatio),
  ] : [], [floor]);

  if (!loading && !floor) {
    return null;
  }

  return (
    <FlatContentCard style={{marginBottom: 24}}>
      {loading && <Center><LoadingSpinner hideBorder={true} hideText={true} /></Center>}
      {floor && <>
        <BodyLarge weight={600} style={{marginBottom: 4}}>{floor.label}</BodyLarge>
        {!floor.hasFloorPlan && <BodyRegular colour={Colours.darkGrey}>{t('reports-page.office-usage.heat-map.no-plan')}</BodyRegular>}
        <HeatMapLegend emptyLabel="reports-page.office-usage.heat-map.legend-not-used"
                       showOverCapacity={ratios.some(ratio => ratio !== undefined && ratio > 1)}
                       showNoCapacity={ratios.some(ratio => ratio === undefined)} />
        {floor.hasFloorPlan ? <HeatMapFloorPlan floor={floor} /> : <DeskHeatTiles floor={floor} />}
        <BodySmall colour={Colours.darkGrey} style={{marginTop: 12}}>
          {t('reports-page.office-usage.heat-map.floor-note', {days: floor.daysInRange})}
        </BodySmall>
      </>}
    </FlatContentCard>
  );
}
